import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { Task, WAYPOINT_COLORS, WAYPOINT_TYPE_NAMES } from '../types';

interface UseTaskMarkersOptions {
  map: maplibregl.Map | null;
  task?: Task;
  showTask?: boolean;
  is3D?: boolean;
}

export const useTaskMarkers = ({ map, task, showTask = true, is3D = false }: UseTaskMarkersOptions) => {
  const taskLayersRef = useRef<string[]>([]);
  const taskSourcesRef = useRef<string[]>([]);
  const previousTaskRef = useRef<Task | undefined>(undefined);

  // Helper function to clean up layers and sources
  const cleanup = useCallback(() => {
    // Remove layers first (must be done before removing sources)
    taskLayersRef.current.forEach(layerId => {
      if (map && map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    });

    // Then remove sources
    taskSourcesRef.current.forEach(sourceId => {
      if (map && map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    });

    taskLayersRef.current = [];
    taskSourcesRef.current = [];
  }, [map]);

  useEffect(() => {
    if (!map || !task || !showTask) {
      cleanup();
      return;
    }

    // Function to add task visualization
    const addTaskVisualization = () => {
      try {
        // Clear previous task visualization
        cleanup();
      } catch (error) {
        console.warn('Error during cleanup:', error);
      }

      try {
        // Create line coordinates for task route
        const lineCoordinates = task.waypoints.map(wp => wp.coordinates);

        // Add task line first (so it appears under circles)
        if (lineCoordinates.length > 1) {
        const lineSourceId = 'task-lines';

        map.addSource(lineSourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: lineCoordinates
            },
            properties: {}
          }
        });

        map.addLayer({
          id: 'task-lines',
          type: 'line',
          source: lineSourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#FF6B6B',
            'line-width': 3,
            'line-dasharray': [2, 2],
            'line-opacity': 0.7
          }
        });

        taskSourcesRef.current.push(lineSourceId);
        taskLayersRef.current.push('task-lines');
      }

      // Add task waypoints with circles and labels
      task.waypoints.forEach((waypoint, index) => {
        const [lng, lat] = waypoint.coordinates;
        const waypointColor = WAYPOINT_COLORS[waypoint.type] || '#0066ff';

        // Create radius circle for each waypoint
        const circleId = `waypoint-circle-${index}`;
        const radiusInKm = waypoint.radius / 1000;

        // Simple circle approximation with proper longitude scaling
        const circleCoords: number[][] = [];
        for (let i = 0; i <= 64; i++) {
          const angle = (i / 64) * 2 * Math.PI;
          const dx = radiusInKm * Math.cos(angle) / (111.32 * Math.cos(lat * Math.PI / 180));
          const dy = radiusInKm * Math.sin(angle) / 111.32;
          circleCoords.push([lng + dx, lat + dy]);
        }

        map.addSource(circleId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [circleCoords]
            },
            properties: {}
          }
        });

        // Add filled circle layer
        map.addLayer({
          id: circleId,
          type: 'fill',
          source: circleId,
          paint: {
            'fill-color': waypointColor,
            'fill-opacity': 0.15
          }
        });

        // Add circle border layer
        map.addLayer({
          id: `${circleId}-border`,
          type: 'line',
          source: circleId,
          paint: {
            'line-color': waypointColor,
            'line-width': 2,
            'line-opacity': 0.6
          }
        });

        taskSourcesRef.current.push(circleId);
        taskLayersRef.current.push(circleId, `${circleId}-border`);

        // Add text label for waypoint (only if glyphs are available and not in 3D mode)
        const labelId = `waypoint-label-${index}`;
        const labelText = WAYPOINT_TYPE_NAMES[waypoint.type] || `WP${index + 1}`;

        // Check if the current style supports text rendering and we're not in 3D
        const style = map.getStyle();
        if (!is3D && style && style.glyphs) {
          map.addSource(labelId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [lng, lat]
              },
              properties: {
                title: labelText,
                name: waypoint.name,
                radius: `${waypoint.radius}m`
              }
            }
          });

          try {
            map.addLayer({
              id: labelId,
              type: 'symbol',
              source: labelId,
              layout: {
                'text-field': ['get', 'title'],
                'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                'text-size': 11,
                'text-offset': [0, 0],
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true
              },
              paint: {
                'text-color': '#ffffff',
                'text-halo-color': waypointColor,
                'text-halo-width': 2
              }
            });

            taskSourcesRef.current.push(labelId);
            taskLayersRef.current.push(labelId);
          } catch (error) {
            console.warn('Could not add text label, glyphs may not be available:', error);
            // Clean up the source if layer failed
            if (map.getSource(labelId)) {
              map.removeSource(labelId);
            }
          }
        }

        // Always add a small colored circle as a marker at waypoint center
        // This stays visible in both 2D and 3D modes
        const markerId = `waypoint-marker-${index}`;

        map.addSource(markerId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [lng, lat]
            },
            properties: {}
          }
        });

        map.addLayer({
          id: markerId,
          type: 'circle',
          source: markerId,
          paint: {
            'circle-radius': is3D ? 6 : 5, // Small, subtle marker
            'circle-color': '#ffffff',  // White center
            'circle-stroke-width': 2,
            'circle-stroke-color': waypointColor,  // Colored border
            'circle-opacity': is3D ? 0.9 : 0.8,
            'circle-stroke-opacity': is3D ? 0.9 : 0.7
          }
        });

        taskSourcesRef.current.push(markerId);
        taskLayersRef.current.push(markerId);

        // Add click handler for waypoint circle
        map.on('click', circleId, (e) => {
          e.preventDefault();

          // Only zoom in if we're currently zoomed out
          const currentZoom = map.getZoom();
          const targetZoom = Math.max(currentZoom, 14); // Don't zoom out, only zoom in

          // Fly to the waypoint location
          map.flyTo({
            center: [lng, lat],
            zoom: targetZoom,
            duration: 1500
          });

          // Create and show popup for waypoint
          const popup = new maplibregl.Popup({
            closeButton: true,
            closeOnClick: true,
            offset: [0, -10],
            className: 'waypoint-popup'
          });

          const popupContent = `
            <div style="padding: 8px; min-width: 180px;">
              <h3 style="margin: 0 0 8px 0; color: ${waypointColor}; font-size: 14px; font-weight: 600;">
                ${labelText} - ${waypoint.name || 'Waypoint'}
              </h3>
              <div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px;">
                <div><strong>Type:</strong> ${WAYPOINT_TYPE_NAMES[waypoint.type] || 'Unknown'}</div>
                <div><strong>Radius:</strong> ${waypoint.radius}m</div>
                <div><strong>Coordinates:</strong></div>
                <div style="font-family: monospace; font-size: 11px; margin-left: 10px;">
                  ${lat.toFixed(5)}°, ${lng.toFixed(5)}°
                </div>
                ${waypoint.seq !== undefined ? `<div><strong>Sequence:</strong> ${waypoint.seq + 1}</div>` : ''}
              </div>
            </div>
          `;

          popup
            .setLngLat([lng, lat])
            .setHTML(popupContent)
            .addTo(map);
        });

        // Change cursor on hover
        map.on('mouseenter', circleId, () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', circleId, () => {
          map.getCanvas().style.cursor = '';
        });
      });

      // Fit map to show all waypoints when task changes
      if (task.waypoints.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        task.waypoints.forEach(wp => {
          bounds.extend(wp.coordinates);
        });

        // Check if this is a new task selection (different from previous)
        const isNewTaskSelection = !previousTaskRef.current ||
          previousTaskRef.current.task_name !== task.task_name;

        // Fit bounds to show the entire task when it's newly selected
        if (isNewTaskSelection) {
          map.fitBounds(bounds, {
            padding: 100,
            maxZoom: 15,
            duration: 1500
          });
        }
      }
      } catch (error) {
        console.error('Error adding task visualization:', error);
        // Try to clean up if something went wrong
        try {
          cleanup();
        } catch (cleanupError) {
          console.warn('Error during cleanup after failed visualization:', cleanupError);
        }
      }
    };

    // Add task visualization initially
    addTaskVisualization();

    // Update the previous task reference
    previousTaskRef.current = task;

    // Re-add task visualization when style changes
    const handleStyleData = () => {
      // Wait for style to be fully loaded
      if (!map.isStyleLoaded()) {
        // Schedule a retry instead of just returning
        setTimeout(() => {
          if (map && map.isStyleLoaded()) {
            handleStyleData();
          }
        }, 50);
        return;
      }

      // Check if our task layers are missing (which means style changed)
      // Check for either task-lines or the first waypoint circle
      const needsReAdd = task.waypoints.length > 0 &&
                         !map.getLayer('waypoint-circle-0') &&
                         !map.getLayer('task-lines');

      if (needsReAdd) {
        console.log('Re-adding task visualization after style change');
        // Small delay to ensure style is fully ready
        setTimeout(() => {
          if (map && map.isStyleLoaded()) {
            addTaskVisualization();
          }
        }, 100);
      }
    };

    // Listen for style changes
    map.on('style.load', handleStyleData);
    map.on('styledata', handleStyleData);
    map.on('idle', handleStyleData);

    // Cleanup function
    return () => {
      cleanup();
      map.off('style.load', handleStyleData);
      map.off('styledata', handleStyleData);
      map.off('idle', handleStyleData);

      // Remove event listeners for click and hover
      task.waypoints.forEach(() => {
        // Event listeners are automatically cleaned up when layers are removed
      });
    };
  }, [map, task, showTask, is3D, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);
};